import { useMutation } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { endpoints } from "@/core/api/endpoints";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import type {
  TransactionFeeQuoteRequestDto,
  TransactionFeeQuoteResponseDto,
} from "@/features/wallet/api/dto";

/**
 * Corps Postman `FeeQuoteRequestDto` : tous les champs du contrat, y
 * compris `transactionReference` (requis à la désérialisation). UUID
 * généré si l'appelant n'en fournit pas — le devis précède l'initiate,
 * donc pas encore de `FPT-…` wallet.
 */
const toFeeQuoteBody = (
  request: TransactionFeeQuoteRequestDto
): TransactionFeeQuoteRequestDto => ({
  productCode: request.productCode,
  providerId: request.providerId ?? null,
  customerSegment: request.customerSegment,
  feePurpose: request.feePurpose,
  amountMinor: request.amountMinor,
  currency: request.currency,
  transactionReference:
    request.transactionReference.trim() || crypto.randomUUID(),
  beneficiaryPartyId: request.beneficiaryPartyId ?? null,
  promoCode: request.promoCode ?? null,
  partyId: request.partyId ?? null,
});

/**
 * Devis de frais d'une transaction (`POST /fee-quotes`, fee-engine §6.5) —
 * alimente la section frais de la ShareablePreview avant initiation.
 *
 * Réplique le hook `useFeeQuote` de fee-engine via le registre
 * `@/core/api/endpoints` (règle : pas d'import croisé entre features).
 */
export const useTransactionFeeQuote = () =>
  useMutation({
    mutationKey: ["wallet", "fee-quotes", "create"],
    mutationFn: (body: TransactionFeeQuoteRequestDto) =>
      mutateIdempotent<TransactionFeeQuoteResponseDto>({
        scopeId: "wallet.fee-quotes",
        url: endpoints.feeEngine.feeQuotes.create,
        body: toFeeQuoteBody(body),
      }),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useTransactionFeeQuote failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
